/**
 * Poll a background admin job (sync / clear / effort estimation) to
 * completion. See server/src/services/jobRunner.js for why these endpoints
 * return 202 + jobId instead of holding the request open: anything past
 * ~30s used to come back to the browser as "Request failed with status code
 * 504" even though the work kept running server-side.
 */
export const waitForJob = async (
  api,
  jobId,
  { timeoutMs = 15 * 60 * 1000, intervalMs = 2000, onProgress } = {}
) => {
  const startedAt = Date.now();
  let lastProgress;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error('Job is still running — check back shortly.');
    }

    let job;
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await api.get(`/jobs/${jobId}`);
      job = data.job;
    } catch (error) {
      // A 404 here means the job id is wrong or the backend restarted mid-run
      // (job state is in-memory, see jobRunner.js) -- surface that plainly
      // instead of polling forever against a job that will never appear.
      throw new Error(error.response?.data?.message || error.message || 'Failed to check job status');
    }

    if (onProgress && job.progress && job.progress !== lastProgress) {
      onProgress(job.progress);
      lastProgress = job.progress;
    }

    if (job.status === 'succeeded') return job;
    if (job.status === 'failed') throw new Error(job.error || 'Job failed');

    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};
